/**
 * commandRegistry — the SINGLE manifest of writer-facing slash
 * commands. The agent (real backend agent later, local heuristic
 * today) reads this manifest to decide which ``/<command>`` to
 * suggest given (a) the writer's free-text intent, or (b) a
 * post-action state transition.
 *
 * Spec 9.5 (Bundle B+D, 2026-04-27) — the user's principle:
 *
 *   "this would be like another tool call that the agent will call
 *    to understand the different '/' options and respond
 *    accordingly. basically i dont want to hardcode any workflow."
 *
 * SRP: pure data + the per-command predicates that DECLARE when
 * each command makes sense. No suggester logic, no rendering, no
 * React. ``commandSuggester`` reads this manifest and is a small
 * generic iterator — adding a new slash command = ONE entry here,
 * never any change to the suggester or the wiring.
 *
 * # DECISION: predicates colocated with the command (not in the
 * #   suggester).
 * # WHY: the user's "no hardcoded workflow" rule means the SEQUENCE
 * #      doctype → library → draft must not live in code. We
 * #      satisfy that by making the suggester generic ("walk the
 * #      manifest, pick the first whose ``suggestAfter`` fires") and
 * #      letting each command declare its own preconditions. Adding
 * #      ``/protocol`` (which doesn't need a library) = one entry
 * #      with a different ``suggestAfter`` — zero suggester change.
 * # DATE: 2026-04-27
 *
 * # DECISION: ``intentKeywords`` is a plain string array, not a
 * #   regex / classifier / embedding lookup.
 * # WHY: MVP scope. The user's vision is an LLM agent picking
 * #      from this manifest; ``intentKeywords`` is the local
 * #      stand-in until that backend tool call exists. When the
 * #      agentic suggestion lands, this field becomes the prompt
 * #      hint shown to the LLM (still useful, never wasted).
 * # DATE: 2026-04-27
 */

// ---------------------------------------------------------------------------
// Snapshot the suggester sees — enough signal to decide, no more
// ---------------------------------------------------------------------------

/** Pure snapshot of the addin state the suggester needs. Keep this
 *  narrow — every field added is a new thing every command might
 *  need to consider. Add only when a real command's ``suggestAfter``
 *  needs the signal. */
export interface CommandSnapshot {
  /** Has the writer picked a doctype yet? */
  hasDoctype: boolean;
  /** Has the writer picked a specific library? */
  hasLibrary: boolean;
  /** Are there any libraries at all in the active workspace? */
  hasAnyLibrary: boolean;
  /** Top-level run state. ``post-run`` is post-success; ``mid-run``
   *  is drafting in progress; ``pausing`` is mid-run winding down to
   *  a safe checkpoint; ``idle`` is pre-run. */
  runState: "idle" | "mid-run" | "pausing" | "approval" | "paused" | "post-run";
}

// ---------------------------------------------------------------------------
// Command shape
// ---------------------------------------------------------------------------

/** Per-command guidance copy. Each command owns its own writer-
 *  facing prose so the renderer doesn't have to template state
 *  awareness — the command itself encodes what good guidance looks
 *  like for its own flow. Best practice for chat-agent guidance:
 *  acknowledge intent → name the action → set expectations.
 */
export interface CommandGuidance {
  /** Emitted as ``agent_note`` when the command was surfaced via
   *  free-text intent matching (Bundle D). Should acknowledge the
   *  writer's intent, name the slash command, and tell them what
   *  to expect after invoking it (especially the prerequisite
   *  steps the writer will be walked through). */
  intent: string;
  /** Emitted as ``agent_note`` when the command was surfaced via a
   *  post-action state transition (Bundle B) — i.e. the writer just
   *  completed a step that made this command the next obvious one.
   *  No need to re-describe the prior step; just point at the next
   *  action. */
  postAction: string;
}

export interface Command {
  /** Stable identifier — also the suggestion id passed in chat events. */
  id: string;
  /** Writer-facing slash syntax (e.g. ``"/draft"``). */
  syntax: string;
  /** Keywords / phrases that hint the writer's free-text describes
   *  this command's intent. Lower-case; matched against the writer's
   *  text by ``suggestFromText`` (the local fallback heuristic). */
  intentKeywords: ReadonlyArray<string>;
  /** One-sentence description of WHEN a writer's intent matches this
   *  command, written for the Haiku classifier. The LLM reads this
   *  alongside ``syntax`` + ``id`` and picks the best match (or null).
   *  Phrase as a condition: "Writer wants to ...". The keywords are
   *  the offline fallback; this field is the agentic path. */
  intentDescription: string;
  /** Per-trigger guidance copy. See ``CommandGuidance``. */
  guidance: CommandGuidance;
  /** Pure predicate: given the snapshot, does this command make
   *  sense as the immediate next step? Optional — commands that are
   *  always invokable (no obvious trigger) omit it and only surface
   *  via ``suggestFromText``.
   *
   *  Order in the registry matters: the suggester returns the FIRST
   *  match, so place commands that complete a flow (``draft``)
   *  before ones that prepare it (``library``). */
  suggestAfter?: (state: CommandSnapshot) => boolean;
}

// ---------------------------------------------------------------------------
// The manifest — single source of truth
// ---------------------------------------------------------------------------

export const COMMAND_REGISTRY: ReadonlyArray<Command> = [
  {
    id: "regen",
    syntax: "/regen",
    // Full-section redraft verbs. "rewrite"/"revise" deliberately
    // absent — those phrase edit_section (chat), not a full redraft.
    intentKeywords: ["regen", "redraft", "regenerate"],
    intentDescription:
      "Writer wants to fully redraft/regenerate the section their cursor is in — rerunning the drafting process with the configured instructions plus their comments.",
    guidance: {
      intent:
        "Sounds like you want a full redraft. Put your cursor in the section and type /regen followed by your comments — e.g. /regen tighten the safety narrative.",
      postAction:
        "To fully redraft a section, type /regen with your comments while your cursor is in it.",
    },
    // No suggestAfter — nagging /regen after every run would be noise;
    // free-text intent matching is enough.
  },
  {
    id: "draft",
    syntax: "/draft",
    // Keep these specific to drafting verbs + doctype names.
    // Generic words like "document" / "report" were removed because
    // they collided with library-intent text ("upload the source
    // documents") and stole matches from the library command.
    intentKeywords: ["draft", "write", "start", "begin", "csr", "psur", "cer", "ib"],
    intentDescription:
      "Writer wants to start drafting a regulatory document (CSR, PSUR, CER, IB) — kicking off the run, beginning the writing pass, or asking how to start.",
    guidance: {
      // Intent — writer expressed they want to draft, but may not
      // have set up doctype/library yet. Acknowledge, name the
      // action, and tell them what /draft will walk them through.
      intent:
        "Sounds like you want to draft. Type /draft to get started — I'll walk you through picking a doctype and a library, then you can kick off the run.",
      // Post-action — fired only when both prerequisites are met
      // (see ``suggestAfter``). Skip the prerequisite walkthrough;
      // the writer has already done it.
      postAction: "You're set up — type /draft when you're ready and I'll start the run.",
    },
    // Both prerequisites met + idle → /draft is the next step.
    suggestAfter: (s) => s.runState === "idle" && s.hasDoctype && s.hasLibrary,
  },
  {
    id: "library",
    syntax: "/library",
    intentKeywords: ["library", "source", "sources", "files", "documents", "upload", "select library"],
    intentDescription:
      "Writer wants to manage source documents — picking, creating, or uploading a library of source files that the draft will be built from. Includes asking where to upload sources or how to select a library.",
    guidance: {
      intent:
        "Sounds like you want to set up a library. Type /library to pick an existing one or create a new one — once it has source documents, you can /draft.",
      // Post-action — fires after the writer picks a doctype but
      // before a library is set. Point at the next obvious step.
      postAction: "Next, type /library to pick an existing library or create a new one. Once it's set, you can /draft.",
    },
    // Doctype set but no library yet → /library is the next step.
    suggestAfter: (s) => s.runState === "idle" && s.hasDoctype && !s.hasLibrary,
  },
  {
    id: "images",
    syntax: "/images",
    // Figure/image nouns only — kept clear of /draft and /library so it
    // doesn't steal "draft the figure caption" or library-intent text.
    intentKeywords: [
      "image",
      "images",
      "figure",
      "figures",
      "diagram",
      "kaplan-meier plot",
      "forest plot",
      "study schema",
      "exhibit",
    ],
    intentDescription:
      "Writer wants to find and insert a FIGURE/IMAGE that already exists in the source library (e.g. a Kaplan-Meier plot, study schema, forest plot) — browsing the library's figures and dropping one into the document. NOT managing source documents (that's /library).",
    guidance: {
      intent:
        "Sounds like you want a figure from your source library. Type /images to browse the library's figures by file or keyword, then click to insert one at your cursor.",
      postAction: "Type /images whenever you want to find and insert a figure from the source library.",
    },
    // Surfaces via free-text intent only (no state-gated post-action).
  },
  // NB: /abbreviations is deliberately NOT registered here. This manifest
  // feeds the intent-classifier LLM (free-text → command suggestion);
  // /abbreviations is an explicit-only command that must never be routed
  // through the chat agent. It lives in the grammar + the /-palette menu
  // only.
];

// ---------------------------------------------------------------------------
// Lookup helpers — pure
// ---------------------------------------------------------------------------

/** Find a command by id. Returns null when the id isn't in the
 *  manifest (caller treats as "no suggestion"). */
export function findCommandById(id: string): Command | null {
  return COMMAND_REGISTRY.find((c) => c.id === id) ?? null;
}
